import { useEffect, useState } from "react";

// Hooks & Helpers
import { useToast } from "sj-ui-toolkit/src/hooks";
import { fetchActivityMessageSettings, saveActivityMessageSetting } from "@/api/activities";

// Local Components
import TabSkeleton from "./TabSkeleton";

// 3rd Party Imports
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";

const MESSAGE_TYPES = {
  PASSED: "dojoActivityPassedMessage",
  FAILED: "dojoActivityFailedMessage",
  NEED_MORE_INFO: "dojoActivityNeedMoreInfoMessage",
} as const;

type MessageKey = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];

interface CustomMessagingTabProps {
  onUnsavedChange?: (hasChanges: boolean) => void;
}

export default function CustomMessagingTab({ onUnsavedChange }: CustomMessagingTabProps) {
  const [messages, setMessages] = useState<Record<MessageKey, string>>({
    [MESSAGE_TYPES.PASSED]: "",
    [MESSAGE_TYPES.FAILED]: "",
    [MESSAGE_TYPES.NEED_MORE_INFO]: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const { showToast } = useToast();

  const markChanged = () => onUnsavedChange?.(true);
  const markSaved = () => onUnsavedChange?.(false);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await fetchActivityMessageSettings();
      const settings = data.settings || {};

      setMessages({
        [MESSAGE_TYPES.PASSED]: settings[MESSAGE_TYPES.PASSED] || "",
        [MESSAGE_TYPES.FAILED]: settings[MESSAGE_TYPES.FAILED] || "",
        [MESSAGE_TYPES.NEED_MORE_INFO]: settings[MESSAGE_TYPES.NEED_MORE_INFO] || "",
      });
    } catch (error) {
      console.error("Failed to load settings:", error);
      showToast({ severity: "error", detail: "Failed to load settings" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      for (const [key, value] of Object.entries(messages)) {
        const snakeCaseName = key.replace(/([A-Z])/g, "_$1").toLowerCase();
        await saveActivityMessageSetting(snakeCaseName, value);
      }
      showToast({ severity: "success", detail: "Custom messages saved successfully" });
      markSaved();
    } catch (error) {
      console.error("Failed to save custom messages:", error);
      showToast({ severity: "error", detail: "Failed to save custom messages" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async () => {
    try {
      for (const key of Object.values(MESSAGE_TYPES)) {
        const snakeCaseName = key.replace(/([A-Z])/g, "_$1").toLowerCase();
        await saveActivityMessageSetting(snakeCaseName, null);
      }
      await loadSettings();
      markSaved();
      showToast({ severity: "success", detail: "Custom messages restored to defaults" });
    } catch (error) {
      console.error("Failed to restore custom messages:", error);
      showToast({ severity: "error", detail: "Failed to restore custom messages" });
    }
  };

  const handleChange = (key: MessageKey, value: string) => {
    setMessages(prev => ({ ...prev, [key]: value }));
    markChanged();
  };

  if (isLoading) {
    return <TabSkeleton rows={3} />;
  }

  return (
    <div className="p-4">
      <Card>
        <div className="mb-4">
          <p className="text-secondary mb-4">
            Customize the messages displayed when activities are reviewed.
          </p>

          <div className="flex flex-col gap-4">
            <div className="field">
              <label htmlFor="passedMessage" className="font-semibold block mb-2">
                Activity Passed
              </label>
              <InputText
                id="passedMessage"
                value={messages[MESSAGE_TYPES.PASSED]}
                onChange={e => handleChange(MESSAGE_TYPES.PASSED, e.target.value)}
                placeholder="Enter custom message for passed activities"
                className="w-full"
                maxLength={120}
              />
            </div>

            <div className="field">
              <label htmlFor="failedMessage" className="font-semibold block mb-2">
                Activity Failed
              </label>
              <InputText
                id="failedMessage"
                value={messages[MESSAGE_TYPES.FAILED]}
                onChange={e => handleChange(MESSAGE_TYPES.FAILED, e.target.value)}
                placeholder="Enter custom message for failed activities"
                className="w-full"
                maxLength={120}
              />
            </div>

            <div className="field">
              <label htmlFor="needInfoMessage" className="font-semibold block mb-2">
                Need More Information
              </label>
              <InputText
                id="needInfoMessage"
                value={messages[MESSAGE_TYPES.NEED_MORE_INFO]}
                onChange={e => handleChange(MESSAGE_TYPES.NEED_MORE_INFO, e.target.value)}
                placeholder="Enter custom message when more information is needed"
                className="w-full"
                maxLength={120}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4 justify-end">
          <Button
            label="Restore Defaults"
            onClick={() => void handleRestore()}
            severity="secondary"
          />
          <Button
            label={isSaving ? "Saving..." : "Save"}
            onClick={() => void handleSave()}
            disabled={isSaving}
          />
        </div>
      </Card>
    </div>
  );
}
